import React from 'react';
import { ResultComponentProps } from '../common/types';
import BaseResultDisplay from '../common/BaseResultDisplay';
import { Typography, Modal, Button, Tag, Tooltip, Space, Table, message, Card } from 'antd';
import { EyeOutlined, CopyOutlined } from '@ant-design/icons';
import BatchSequenceResult from './BatchSequenceResult';

const { Text, Paragraph } = Typography;

// 序列转换结果组件
const SequenceConversionResult: React.FC<ResultComponentProps> = ({
  data,
  title,
  onDeleteResult,
  multiResults = false,
  resultIndex,
}) => {
  // 检查是否为批量结果 - 支持新旧两种格式
  const isBatchResult = (data?.items && Array.isArray(data.items) && data.count !== undefined) ||
                        (data?.results && Array.isArray(data.results) && data.total_sequences !== undefined);
  
  // 如果是批量结果，使用批量组件
  if (isBatchResult) {
    return (
      <BatchSequenceResult
        data={data}
        title={title || '批量序列转换结果'}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        resultIndex={resultIndex}
      />
    );
  }

  // 原有的单序列转换逻辑保持不变
  const [viewModalVisible, setViewModalVisible] = React.useState(false);
  const [currentSequence, setCurrentSequence] = React.useState<{original: string, converted: string, name?: string, originalId?: string} | null>(null);

  // 准备表格数据
  const prepareTableData = () => {
    // 如果是单个结果对象
    if (!Array.isArray(data)) {
      return [{
        key: '0',
        name: data.originalId || '>序列1',
        originalId: data.originalId || '>序列1',
        original_sequence: data.original_sequence,
        from_type: data.from_type,
        converted_sequence: data.converted_sequence,
        to_type: data.to_type,
      }];
    }
    
    // 如果是数组，可能是多个结果或者已经合并的结果
    return data.flatMap((item, index) => {
      // 检查item是否是一个结果对象或者包含多个结果的数组
      if (item.original_sequence && item.converted_sequence) {
        // 完全保留原始ID，不做任何修改
        const originalId = item.originalId || `>序列${index + 1}`;
        
        // 单个结果对象
        return [{
          key: `${index}`,
          name: originalId, // 使用原始ID作为名称
          originalId: originalId,
          original_sequence: item.original_sequence,
          from_type: item.from_type,
          converted_sequence: item.converted_sequence,
          to_type: item.to_type,
        }];
      }
      // 如果item本身没有转换字段，可能是一个包装对象
      return [];
    });
  };

  const tableData = prepareTableData();

  // 查看完整序列
  const handleViewSequence = (record: any) => {
    setCurrentSequence({
      original: record.original_sequence,
      converted: record.converted_sequence,
      name: record.name,
      originalId: record.originalId
    });
    setViewModalVisible(true);
  };

  // 表格列定义
  const columns = [
    {
      title: '序列名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      ellipsis: true,
      render: (text: string) => (
        <Text style={{ fontFamily: 'monospace' }}>{text}</Text>
      ),
    },
    {
      title: '输入序列',
      dataIndex: 'original_sequence',
      key: 'original_sequence',
      render: (text: string, record: any) => (
        <Space>
          <Text style={{ fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
            {text && text.length > 30 ? `${text.substring(0, 30)}...` : (text || '')}
          </Text>
          <Tooltip title="查看完整序列">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              size="small" 
              onClick={() => handleViewSequence(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: '输入类型',
      dataIndex: 'from_type',
      key: 'from_type',
      width: 100,
      render: (text: string) => (
        <Text style={{ textTransform: 'uppercase' }}>{text}</Text>
      ),
    },
    {
      title: '转换后序列',
      dataIndex: 'converted_sequence',
      key: 'converted_sequence',
      render: (text: string, record: any) => (
        <Space>
          <Text style={{ fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
            {text && text.length > 30 ? `${text.substring(0, 30)}...` : (text || '')}
          </Text>
          <Tooltip title="查看完整序列">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              size="small" 
              onClick={() => handleViewSequence(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: '转换后类型',
      dataIndex: 'to_type',
      key: 'to_type',
      width: 100,
      render: (text: string) => {
        // 简化类型显示，只显示DNA或RNA
        let simplifiedType = text;
        if (text && typeof text === 'string') {
          // 如果包含DNA，则显示为DNA
          if (text.toUpperCase().includes('DNA')) {
            simplifiedType = 'DNA';
          }
          // 如果包含RNA，则显示为RNA
          else if (text.toUpperCase().includes('RNA')) {
            simplifiedType = 'RNA';
          }
        }
        return <Text style={{ textTransform: 'uppercase' }}>{simplifiedType}</Text>;
      },
    },
  ];

  // 复制所有结果到剪贴板
  const handleCopyAll = () => {
    try {
      // 复制为FASTA格式，使用原始ID
      const textToCopy = tableData.map(row => {
        // 保持原始ID不变
        const id = row.originalId;
        return `${id}\n${row.converted_sequence}`;
      }).join('\n');
      
      // 使用document.execCommand方法复制，兼容性更好
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      document.body.appendChild(textArea);
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        message.success('已复制所有结果到剪贴板（FASTA格式）');
      } else {
        message.error('复制失败，请手动复制');
      }
    } catch (err) {
      console.error('复制失败:', err);
      message.error('复制失败，请手动复制');
    }
  };

  // 复制当前查看的序列
  const handleCopyCurrentSequence = () => {
    if (currentSequence) {
      try {
        // 使用原始ID，不做任何修改
        const id = currentSequence.originalId || '>序列';
        const textToCopy = `${id}\n${currentSequence.converted}`;
        
        // 使用document.execCommand方法复制，兼容性更好
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          message.success('已复制序列到剪贴板（FASTA格式）');
        } else {
          message.error('复制失败，请手动复制');
        }
      } catch (err) {
        console.error('复制失败:', err);
        message.error('复制失败，请手动复制');
      }
    }
  };

  // 复制为表格格式
  const handleCopyAsTable = () => {
    try {
      // 复制为表格格式（制表符分隔）
      const header = "序列ID\t输入序列\t输入类型\t转换后序列\t转换后类型";
      const rows = tableData.map(row => {
        // 保持原始ID不变
        const id = row.originalId;
        
        // 简化类型显示，只显示DNA或RNA
        let simplifiedType = row.to_type;
        if (row.to_type && typeof row.to_type === 'string') {
          // 如果包含DNA，则显示为DNA
          if (row.to_type.toUpperCase().includes('DNA')) {
            simplifiedType = 'DNA';
          }
          // 如果包含RNA，则显示为RNA
          else if (row.to_type.toUpperCase().includes('RNA')) {
            simplifiedType = 'RNA';
          }
        }
        
        return `${id}\t${row.original_sequence}\t${row.from_type}\t${row.converted_sequence}\t${simplifiedType}`;
      }).join('\n');
      
      const textToCopy = `${header}\n${rows}`;
      
      // 使用document.execCommand方法复制
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      document.body.appendChild(textArea);
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        message.success('已复制所有结果到剪贴板（表格格式）');
      } else {
        message.error('复制失败，请手动复制');
      }
    } catch (err) {
      console.error('复制失败:', err);
      message.error('复制失败，请手动复制');
    }
  };

  return (
    <BaseResultDisplay
      title={title || "序列转换结果"}
      data={data}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
      showRawData={false}
      showDistribution={false}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Text type="secondary">共 {tableData.length} 条序列转换结果</Text>
          <Space>
            <Button 
              icon={<CopyOutlined />} 
              onClick={handleCopyAsTable}
            >
              复制为表格
            </Button>
            <Button 
              type="primary" 
              icon={<CopyOutlined />} 
              onClick={handleCopyAll}
            >
              复制为FASTA
            </Button>
          </Space>
        </div>
        
        <Table 
          columns={columns} 
          dataSource={tableData} 
          pagination={false}
          scroll={{ x: 'max-content' }}
          size="middle"
          bordered
        />
      </Space>

      {/* 查看完整序列的模态框 */}
      <Modal
        title={currentSequence?.originalId || "完整序列"}
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="copy" type="primary" icon={<CopyOutlined />} onClick={handleCopyCurrentSequence}>
            复制序列
          </Button>,
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {currentSequence && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Card title="输入序列" size="small">
              <div 
                style={{ 
                  fontFamily: 'monospace', 
                  whiteSpace: 'pre-wrap', 
                  maxHeight: '200px', 
                  overflowY: 'auto',
                  padding: '8px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px'
                }}
              >
                {currentSequence.original}
              </div>
            </Card>
            <Card title="转换后序列" size="small">
              <div 
                style={{ 
                  fontFamily: 'monospace', 
                  whiteSpace: 'pre-wrap', 
                  maxHeight: '200px', 
                  overflowY: 'auto',
                  padding: '8px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px'
                }}
              >
                {currentSequence.converted}
              </div>
            </Card>
          </Space>
        )}
      </Modal>
    </BaseResultDisplay>
  );
};

export default SequenceConversionResult;